"use client";

import { useScrollReveal } from "@/src/hooks/useScrollReveal";
import { PersianNum } from "@/src/tools";
import {
    ProductIdentityProductListType,
    RowProductListType,
} from "@/src/types/product";
import clsx from "clsx";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useRef, useState } from "react";

const ITEM_SIZE = 48;
const ITEM_GAP = 8;

const loadProductIdentity = (
    identities: ProductIdentityProductListType[],
    id: number | null
) => {
    const selectedIdentity =
        identities.find((i) => (id !== null ? i.id === id : false)) ||
        identities[0];

    const mainAlbum = selectedIdentity?.Albums?.[0] || {
        album_image_alt: "Product image",
        album_image_src: "/default-product-image.png",
    };
    const warehouse = selectedIdentity?.Warehouses?.[0] || {
        warehouse_price: 0,
        warehouse_discount: 0,
    };
    return { img: mainAlbum, warehouse };
};

interface CardProductProps {
    data: RowProductListType;
    active?: number[];
}

const CardProduct: React.FC<any> = ({ data, active = [] }) => {
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [showAll, setShowAll] = useState<boolean>(false);

    const revealRef: any = useScrollReveal();
    const sliderRef = useRef<HTMLDivElement>(null);

    const [isDown, setIsDown] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScroll] = useState(0);

    useEffect(() => {
        if (active.length > 0) {
            setShowAll(true);
            const found = data.ProductIdentities.find((i: any) =>
                i.ProdProperties?.some((pp: any) =>
                    active.includes(pp.Attribute?.id ?? -1)
                )
            );
            setSelectedId(found?.id ?? null);
        } else {
            setShowAll(false);
            setSelectedId(null);
        }
    }, [data.ProductIdentities]);

    const productData = loadProductIdentity(data.ProductIdentities, selectedId);
    const { warehouse } = productData;

    const getBorderColor = (i: ProductIdentityProductListType) => {
        const raw = i.ProdProperties?.[0]?.Attribute?.attribute_label || "#223843";
        return raw.startsWith("#") ? raw : `#${raw}`;
    };

    const totalVariants = data.ProductIdentities.length;
    const visibleCount = showAll ? totalVariants : Math.min(3, totalVariants);
    const identitiesToShow = data.ProductIdentities.slice(0, visibleCount);
    const remainingCount = totalVariants - visibleCount;

    const discountPercent =
        warehouse.warehouse_price > 0
            ? Math.round(
                ((warehouse.warehouse_price - warehouse.warehouse_discount) /
                    warehouse.warehouse_price) *
                100
            )
            : 0;
    const discountFa = PersianNum(discountPercent);

    const maxWidthValue =
        visibleCount * ITEM_SIZE + (visibleCount - 1) * ITEM_GAP;

    // Drag Handlers
    const handleMouseDown = (e: React.MouseEvent) => {
        if (!showAll || !sliderRef.current) return;
        setIsDown(true);
        setStartX(e.pageX - sliderRef.current.offsetLeft);
        setScroll(sliderRef.current.scrollLeft);
    };
    const stopDrag = () => setIsDown(false);
    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDown || !sliderRef.current) return;
        e.preventDefault();
        const x = e.pageX - sliderRef.current.offsetLeft;
        sliderRef.current.scrollLeft = scrollLeft - (x - startX);
    };

    return (
        <div
            ref={revealRef}
            className="w-full max-w-sm mx-auto bg-white rounded-xl shadow-lg p-4 m-1 transition hover:shadow-2xl"
        >
            {/* تصویر و عنوان */}
            <Link
                href={`/product/${data.product_url.replace(/ /g, "-")}`}
                className="block cursor-pointer"
            >
                <div className="relative w-full mb-4 overflow-hidden">
                    <div className="relative w-full aspect-square bg-white rounded-md shadow overflow-hidden">
                        <Image
                            fill
                            alt={productData.img.album_image_alt || data.product_title}
                            src={
                                (process.env.IMAGE_URL ?? "") +
                                productData.img.album_image_src ||
                                "/placeholder.png"
                            }
                            priority
                            sizes="(max-width: 600px) 100vw, 180px"
                            className="object-cover"
                        />
                    </div>
                </div>
                <p className="text-center mb-2 text-base font-bold text-[#2E2E2E] truncate">
                    {data.product_title}
                </p>
            </Link>

            {/* نوار واریانت‌ها */}
            <div className="flex items-center justify-center mt-4">
                <div
                    ref={sliderRef}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={stopDrag}
                    onMouseUp={stopDrag}
                    onMouseMove={handleMouseMove}
                    className={clsx(
                        "flex items-center gap-2 py-1 whitespace-nowrap",
                        showAll
                            ? "overflow-x-auto cursor-grab hide-scrollbar"
                            : "overflow-hidden"
                    )}
                    style={{ maxWidth: `${maxWidthValue}px` }}
                >
                    {identitiesToShow.map((idn: any) => {
                        const color = getBorderColor(idn);
                        const isSelected = idn.id === selectedId;
                        const variantImg =
                            idn.Albums?.[0] || {
                                album_image_alt: "Product variant",
                                album_image_src: "/default-variant-image.png",
                            };

                        return (
                            <div
                                key={idn.id}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setSelectedId(idn.id);
                                }}
                                className={clsx(
                                    "cursor-pointer rounded-full w-12 h-12 flex-shrink-0 flex items-center justify-center transition-all border-solid bg-white",
                                    isSelected ? "border-4" : "border-2"
                                )}
                                style={{
                                    borderColor: color,
                                    boxShadow: isSelected ? `0 0 0 2px ${color}80` : "none",
                                }}
                            >
                                <Image
                                    width={40}
                                    height={40}
                                    alt={variantImg.album_image_alt}
                                    src={
                                        (process.env.IMAGE_URL ?? "") +
                                        variantImg.album_image_src
                                    }
                                    className="rounded-full object-cover"
                                />
                            </div>
                        );
                    })}
                </div>

                {/* دکمه +N */}
                {!active.length && remainingCount > 0 && (
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setShowAll((p) => !p);
                        }}
                        className="mr-2 rounded-full border-2 border-[#223843] w-12 h-12 flex items-center justify-center text-[#223843] bg-white hover:bg-[#223843] hover:text-white transition"
                    >
                        <span className="text-sm font-bold">
                            +{PersianNum(remainingCount)}
                        </span>
                    </button>
                )}
            </div>

            {/* قیمت و تخفیف */}
            {warehouse.warehouse_discount > 0 ? (
                <div className="flex items-center justify-between text-center mt-3">
                    <p className="text-[#223843] text-base font-bold mx-2">
                        {PersianNum(warehouse.warehouse_discount)} تومان
                    </p>
                    <div className="flex items-center">
                        <div className="bg-red-600 text-white text-xs font-bold p-1 ml-1 rounded-full z-20">
                            {`${discountFa}%`}
                        </div>
                        <p className="text-[#606060] text-sm line-through">
                            {PersianNum(warehouse.warehouse_price)} تومان
                        </p>
                    </div>
                </div>
            ) : (
                <p className="text-center text-base font-bold mt-3 text-[#223843]">
                    {PersianNum(warehouse.warehouse_price)} تومان
                </p>
            )}
        </div>
    );
};

export default CardProduct;
